import axios from "axios";
import { SERVER_URL } from "../config/index";
import store from "../redux/store";
import actions from "../redux/user/actions";
import CryptoJS from "crypto-js";
import { getCSRFToken, clearCSRFToken } from "../utils/csrfProtection";
import { SECURITY_CONFIG, getSanitizedErrorMessage } from "../config/security";
import { toast } from "react-toastify";

const ENCRYPTION_KEY =
  process.env.REACT_APP_ENCRYPTION_KEY || "paraacryptobackendnodejs";

const request = axios.create({
  baseURL: SERVER_URL,
  timeout: 1000000,
  withCredentials: true, // Enable sending cookies for CSRF
});

let requests = [];
let retryCount = new Map(); // Track retry attempts per request

/**
 * Request Interceptor - Add authentication, CSRF tokens, and security headers
 */
request.interceptors.request.use(
  (config) => {
    // Add authentication token if exists
    if (store.getState().user?.token) {
      const jsonData = JSON.stringify(store.getState().user?.token);
      const token = CryptoJS.AES.encrypt(jsonData, ENCRYPTION_KEY).toString();
      config.headers.Authorization = `${token}`;
    }

    // Add CSRF token for state-changing requests
    if (SECURITY_CONFIG.API.ENABLE_CSRF) {
      const csrfToken = getCSRFToken();
      if (
        csrfToken &&
        ["post", "put", "patch", "delete"].includes(config.method.toLowerCase())
      ) {
        config.headers[SECURITY_CONFIG.API.CSRF_HEADER] = csrfToken;
      }
    }

    // Add security headers
    config.headers["X-Requested-With"] = "XMLHttpRequest";

    // Only set Content-Type if not already set and not FormData
    if (!config.headers["Content-Type"] && !(config.data instanceof FormData)) {
      config.headers["Content-Type"] = "application/json";
    }

    // Show loading indicator
    store.dispatch(actions.setLoading(true));

    // Track request
    requests.push(config);
    return config;
  },
  (error) => {
    store.dispatch(actions.setLoading(false));
    if (SECURITY_CONFIG.ERRORS.LOG_TO_CONSOLE) {
      console.error("Request Error:", error);
    }
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor - Handle errors, rate limiting, and authentication
 */
request.interceptors.response.use(
  function (response) {
    store.dispatch(actions.setLoading(false));

    // Reset retry count on success
    const requestId = `${response.config.method}-${response.config.url}`;
    retryCount.delete(requestId);

    return response;
  },
  function (error) {
    console.log("%c Line:79 🍑 error", "color:#f5ce50", error);
    store.dispatch(actions.setLoading(false));

    const status = error?.response?.status;
    const config = error?.config;
    const requestId = config ? `${config.method}-${config.url}` : null;

    // Handle 401 Unauthorized - Token expired or invalid
    if (status === 401) {
      const errorMessage = error?.response?.data?.message || "";
      if (
        errorMessage === "Invalid token or expired!" ||
        errorMessage === "You are not authorized." ||
        errorMessage === "Invalid session" ||
        errorMessage === "Session expired or invalid. Please login again." ||
        errorMessage === "You are not authorized."
      ) {
        handleLogout();
        toast.error("Session expired. Please login again.", { toastId: 'session-expired' });
        return Promise.reject(error);
      }
    }

    // Handle 403 Forbidden - CSRF token invalid
    if (status === 403) {
      const errorMessage = error?.response?.data?.message || "";
      if (errorMessage.toLowerCase().includes("csrf")) {
        clearCSRFToken();
        toast.error(
          "Security validation failed. Please refresh and try again.",
          { toastId: 'api-error' }
        );
        return Promise.reject(error);
      }
    }

    // Handle 429 Too Many Requests - Rate limiting
    if (status === 429) {
      const retryAfter = error?.response?.headers["retry-after"];
      const rateLimitReset = error?.response?.headers["ratelimit-reset"];
      
      let waitTime = SECURITY_CONFIG.API.RATE_LIMIT_RETRY_AFTER;
      if (retryAfter) {
        waitTime = parseInt(retryAfter) * 1000;
      } else if (rateLimitReset) {
        waitTime = Math.max(0, parseInt(rateLimitReset) * 1000 - Date.now());
      }

      const backendMsg = error?.response?.data?.message;
      if (!backendMsg) {
        toast.warning(
          `Too many requests. Please wait ${Math.ceil(waitTime / 1000)} seconds before trying again.`,
          { toastId: 'api-warning' }
        );
      }

      // We intentionally do NOT retry 429s automatically because it hangs the UI
      // with a loader for the entire duration of the waitTime.
      return Promise.reject(error);
    }

    // Handle 500+ Server Errors
    if (status >= 500) {
      const sanitizedMessage = getSanitizedErrorMessage(error);
      toast.error(sanitizedMessage, { toastId: 'api-error' });

      if (SECURITY_CONFIG.ERRORS.LOG_TO_CONSOLE) {
        console.error("Server Error:", error);
      }
      return Promise.reject(error);
    }

    // Handle Network Errors
    if (!error.response) {
      toast.error("Network error. Please check your internet connection.", { toastId: 'network-error' });
      return Promise.reject(error);
    }

    // Log other errors in development
    if (SECURITY_CONFIG.ERRORS.LOG_TO_CONSOLE) {
      console.error("API Error:", error);
    }

    return Promise.reject(error);
  }
);

/**
 * Handle user logout - clear all session data
 */
function handleLogout() {
  // Clear all storage
  localStorage.clear();
  sessionStorage.clear();

  // Clear CSRF token
  clearCSRFToken();

  // Dispatch logout actions
  store.dispatch(actions.setLoggedIn(false));
  store.dispatch(actions.setUser(null));
  store.dispatch(actions.setToken(null));
  store.dispatch(actions.setRole(null));
  store.dispatch(actions.setPermission([]));
  store.dispatch(actions.setSidebarItems([]));

  // Redirect to login
  window.location.href = "/admin/login";
}

export default request;
